import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import axios from "axios";
import i18n from "../../../i18n/i18n";
import { useLocation, useNavigate } from "react-router-dom";
import SitePageLeftHeading from "../../../components/SitePageLeftHeading";
import noImg from "../../../assets/img/no-img.png";
import toast from "react-hot-toast";
import MediaSelect from "../../../components/form/MediaSelect";
import logo from "../../../assets/img/logo.png";
import logoWhite from "../../../assets/img/logo-white.png";
import { Link } from "react-router-dom";

export default function VerificationDocuments() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const data = state?.data;

  const apiUrl = import.meta.env.VITE_API_URL;
  const contentUrl = import.meta.env.VITE_CONTENT_URL;
  const maxUploadSize = import.meta.env.VITE_MAX_FILE_UPLOAD_SIZE;
  const allowedImgMimes = import.meta.env.VITE_IMG_UPLOAD_MIME;

  const [previews, setPreviews] = useState([noImg, noImg, noImg, noImg])
  const [uploadStates, setUploadStates] = useState([false, false, false, false])
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          apiUrl + "/api/v1/registration/kyc-documents?sessionId=" + data.sessionId
        );
        if (!response.ok) {
          console.log("Error: getting kyc documents failed");
        }
        const result = await response.json();

        const previews_ = [...previews];
        const uploadStates_ = [...uploadStates]

        result.forEach((doc) => {
          previews_[doc.index] = contentUrl + doc.imagePath
          uploadStates_[doc.index] = true
        });
        setPreviews(previews_)
        setUploadStates(uploadStates_)
      } catch (error) {
        console.log("Error: getting kyc documents failed" + error);
      }
    };
    fetchData();
    
  }, []);

  const fileTypes = () => {
    const arrmgMimes = allowedImgMimes.split(",");
    let mimes = [];
    for (const mime of arrmgMimes) {
      let arr = mime.split("/");
      mimes.push(arr.at(-1));
    }
    return mimes.join(",");
  };

  const setUploadState = (ndx, state) => {
    const uploadStates_ = [...uploadStates]
    uploadStates_[ndx] = state
    setUploadStates(uploadStates_)
  }

  const submit = async () => {
    try {
      if (uploadStates[0] && uploadStates[1] && uploadStates[2] && uploadStates[3]

      ) {
        const response = await axios.post(
          apiUrl + "/api/v1/registration/kyc-document/submit",
          {
            sessionId: data.sessionId,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "Accept-Language": i18n.language || "en",
            },
          }
        );

        navigate("/registration/customer-detail", {
          state: { data: response.data },
        });
      } else {
        toast.error(
          t(
            "pages.registration.verification_documents.messages.upload_all_documents"
          )
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  function documentType(ndx, code = true) {
    switch (ndx) {
      case 0:
        return code ? "IDF" : "id_front";

      case 1:
        return code ? "IDB" : "id_back";

      case 2:
        return code ? "SEL" : "photo";

      case 3:
        return code ? "SIG" : "signature";
    }
  }

  return (
    <>
      <Helmet>
        <title>{t("pages.registration.verification_documents.title")}</title>
      </Helmet>

      <div className="relative z-1 flex flex-col items-center justify-center p-6">
        <div className="mx-auto w-full max-w-[600px] text-center sm:max-w-[900px]">
          
          <div className="mt-5 mb-12 w-full text-center">
            <Link to="/" className="block w-fit mx-auto" >
              <img src={logo} alt="Logo" className="block dark:hidden" />
              <img src={logoWhite} alt="Logo" className="hidden dark:block" />
            </Link>
          </div>

          <SitePageLeftHeading
            title={t("pages.registration.verification_documents.page_title")}
            description={t(
              "pages.registration.verification_documents.description"
            )}
          />

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {t("pages.registration.verification_documents.image_formats", {
              formats: fileTypes(),
            })}
            ,
            {t("pages.registration.verification_documents.max_file_size", {
              size: maxUploadSize,
            })}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.id_front")}
              </h4>

              <MediaSelect index={0} documentType={documentType(0, false)} documentCode={documentType(0)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[0]} defaultState={uploadStates[0] ? "C" : "P"} />
            </div>

            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.id_back")}
              </h4>
              
              <MediaSelect index={1} documentType={documentType(1, false)} documentCode={documentType(1)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[1]} defaultState={uploadStates[1] ? "C" : "P"} />
            </div>

            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.photo")}
              </h4>
              
              <MediaSelect index={2} documentType={documentType(2, false)} documentCode={documentType(2)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[2]} defaultState={uploadStates[2] ? "C" : "P"} />
            </div>

            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.signature")}
              </h4>
              
              <MediaSelect index={3} documentType={documentType(3, false)} documentCode={documentType(3)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[3]} defaultState={uploadStates[3] ? "C" : "P"} />
            </div>
          </div>

          <div className="my-6">
            <button
              type="button"
              onClick={() => submit()}
              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
            >
              {t("buttons.continue")}
            </button>
          </div>
        </div>
      </div>

      {/* <div className="flex flex-col flex-1 w-full">
        <LogoWithHomeLink />

        <div className="justify-center w-full max-w-2/3 mx-auto mt-5">
          <SitePageLeftHeading
            title={t("pages.registration.verification_documents.page_title")}
            description={t(
              "pages.registration.verification_documents.description"
            )}
          />

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            {t("pages.registration.verification_documents.image_formats", {
              formats: fileTypes(),
            })}
            ,
            {t("pages.registration.verification_documents.max_file_size", {
              size: maxUploadSize,
            })}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.id_front")}
              </h4>

              <MediaSelect index={0} documentType={documentType(0, false)} documentCode={documentType(0)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[0]} defaultState={uploadStates[0] ? "C" : "P"} />
            </div>

            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.id_back")}
              </h4>
              
              <MediaSelect index={1} documentType={documentType(1, false)} documentCode={documentType(1)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[1]} defaultState={uploadStates[1] ? "C" : "P"} />
            </div>

            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.photo")}
              </h4>
              
              <MediaSelect index={2} documentType={documentType(2, false)} documentCode={documentType(2)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[2]} defaultState={uploadStates[2] ? "C" : "P"} />
            </div>

            <div className="rounded-2xl p-4 border border-gray-300">
              <h4 className="font-semibold text-gray-800 dark:text-white/90">
                {t("verification_documents.types.signature")}
              </h4>
              
              <MediaSelect index={3} documentType={documentType(3, false)} documentCode={documentType(3)} sessionId={data.sessionId} funcUploadStatus={setUploadState} defaultPreview={previews[3]} defaultState={uploadStates[3] ? "C" : "P"} />
            </div>
          </div>

          <div className="my-6">
            <button
              type="button"
              onClick={() => submit()}
              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
            >
              {t("buttons.continue")}
            </button>
          </div>
        </div>
      </div> */}
    </>
  );
}
